import axios from "axios";

// When the SPA is served from a partner's custom domain (e.g.
// pay.faitextiles.com), use the SAME origin for /api/* calls — Caddy
// reverse-proxies /api on every custom domain, so this avoids CORS
// entirely. Fall back to the build-time gateway origin for the canonical
// host (app.elevationgroup.io).
const BUILD_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
function resolveBackend() {
    if (typeof window === "undefined") return BUILD_BACKEND;
    try {
        const built = BUILD_BACKEND ? new URL(BUILD_BACKEND).hostname.toLowerCase() : "";
        const here = window.location.hostname.toLowerCase();
        // Same host as the build-time gateway → keep the explicit origin
        // (preserves protocol/port). Different host (custom domain or
        // localhost) → use current origin so the request is same-origin.
        if (!built || here === built) return BUILD_BACKEND;
        return window.location.origin;
    } catch {
        return BUILD_BACKEND;
    }
}

export const BACKEND_URL = resolveBackend();
export const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// CSRF double-submit: read the `pg_csrf` cookie set by the login response
// and echo it back as `X-CSRF-Token` on every state-mutating call. The
// backend middleware exempts GET/HEAD/OPTIONS + /api/public/* + login
// endpoints so this is safe to apply blanket-style.
function readCookie(name) {
    if (typeof document === "undefined") return "";
    const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : "";
}
const IDEMPOTENT_PATHS = ["/partner/payin/orders", "/partner/payouts/send"];
api.interceptors.request.use((cfg) => {
    const method = (cfg.method || "get").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        const csrf = readCookie("pg_csrf");
        if (csrf) {
            cfg.headers = cfg.headers || {};
            cfg.headers["X-CSRF-Token"] = csrf;
        }
    }
    if (method === "POST" && IDEMPOTENT_PATHS.includes((cfg.url || "").split("?")[0])) {
        cfg.headers = cfg.headers || {};
        if (!cfg.headers["Idempotency-Key"]) {
            cfg.headers["Idempotency-Key"] = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        }
    }
    return cfg;
});

// Auth-aware response interceptor. Without this, a 401 surfaces as a
// generic `AxiosError: Request failed with status code 401` toast which
// is opaque to non-technical users. Now we:
//   1. Detect 401 on any API call (token expired / cookie cleared)
//   2. Redirect to the appropriate login page based on the current path
//      (admin vs partner) so the user lands on the right form
//   3. Preserve the intended path in `?next=` so they bounce back after
//      re-auth instead of dumping them on the dashboard
// Skipped for: the login endpoint itself (401 there means "wrong
// password" and should bubble up to the form) AND any request flagged
// with `_skipAuthRedirect` (e.g. background-refresh calls that should
// fail silently).
api.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        const skip = err?.config?._skipAuthRedirect;
        const isAuthEndpoint = /\/auth\/(partner|admin)\/(login|refresh)|\/auth\/me\b/.test(url);
        if (status === 401 && !skip && !isAuthEndpoint && typeof window !== "undefined") {
            const here = window.location.pathname + window.location.search;
            // ONLY redirect when the user is already inside an
            // authenticated section of the app (/partner/* or /admin/*).
            // PUBLIC pages (/pay/{code}, /, /payment-link/*, etc.) must
            // never bounce to login — they have their own UI for missing
            // auth (or simply don't require auth). Without this guard,
            // a customer hitting their merchant's payment link would be
            // shown "Your session expired" — which is exactly the bug
            // a user reported on app.elevationgroup.io/pay/{code}.
            const isAuthedArea = here.startsWith("/partner/") || here.startsWith("/admin/")
                || here === "/partner" || here === "/admin";
            if (!isAuthedArea) {
                return Promise.reject(err);
            }
            const isAdmin = here.startsWith("/admin");
            const loginPath = isAdmin ? "/admin/login" : "/partner/login";
            // Don't bounce if we're already on the login page (would loop).
            if (!here.startsWith(loginPath)) {
                const next = encodeURIComponent(here);
                window.location.replace(`${loginPath}?expired=1&next=${next}`);
            }
        }
        return Promise.reject(err);
    },
);

export default api;

export function formatDetail(d) {
    if (d == null) return "Something went wrong.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
    if (d?.message) return d.message;
    if (d?.msg) return d.msg;
    return String(d);
}

export function fmtINR(n) {
    if (n == null || isNaN(n)) return "—";
    return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function fmtNum(n, digits = 0) {
    if (n == null || isNaN(n)) return "—";
    return Number(n).toLocaleString("en-IN", { maximumFractionDigits: digits });
}

export function shortDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
}
